import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  Container,
  Card,
  Spinner,
  Row,
  Col,
  Button,
  Table,
  Pagination,
} from "react-bootstrap";
import "../styles/fieldpage.css";
import SubjectForm from "../components/Subject/SubjectForm";
import EditFieldModal from "../components/Field/EditFieldModal";
import DeleteConfirmModal from "../components/Field/DeleteConfirmModal";
import SubjectTree from "../components/Subject/SubjectTree";
import api from "../api.js";

const FieldPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = Cookies.get("access_token");
  const currentuser = Cookies.get("user_id");

  // --- State ---
  const [field, setField] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [subjects, setSubjects] = useState([]);

  // --- Reports state ---
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsTotalPages, setReportsTotalPages] = useState(0);
  const PAGE_SIZE = 10; // Must match DRF pagination page_size

  // --- Fetch field, subjects, and initial reports ---
  useEffect(() => {
    const fetchField = async () => {
      try {
        const res = await api.get(`/field/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setField(res.data);
        setAuthor(res.data.author);
        setLoading(false);
      } catch {
        setError("خطا در دریافت اطلاعات.");
        setLoading(false);
      }
    };

    const fetchSubjects = async () => {
      try {
        const res = await api.get(`/subject/field/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubjects(res.data);
      } catch {
        setSubjects([]);
      }
    };

    fetchField();
    fetchSubjects();
    fetchReports(1);
  }, [id, token]);

  // --- Fetch paginated reports ---
  const fetchReports = async (page) => {
    setReportsLoading(true);
    try {
      const res = await api.get(`/report/field/${id}/?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(res.data.results || []);
      setReportsTotalPages(Math.ceil((res.data.count || 0) / PAGE_SIZE));
      setReportsPage(page);
      setReportsLoading(false);
    } catch {
      setReports([]);
      setReportsTotalPages(0);
      setReportsLoading(false);
    }
  };

  // --- Delete field ---
  const handleDelete = async () => {
    await api.delete(`/field/${id}/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setShowDelete(false);
    navigate("/home");
  };

  if (loading)
    return (
      <div className="loading-container">
        <Spinner animation="border" />
      </div>
    );
  if (error)
    return <div className="loading-container text-danger">{error}</div>;

  return (
    <Container fluid className="field-page-container text-end px-3" dir="rtl">
      <Row className="mb-4 gx-4 gy-4 justify-content-center">
        {/* Profile Section */}
        <Col xs={12} md={4} lg={3} className="d-flex justify-content-center">
          <Card className="profile-card text-center">
            <Card.Body>
              <img
                src={field.thumbnail || "https://via.placeholder.com/600x400"}
                className="rounded-circle profile-img mb-3"
                alt="thumbnail"
              />
              <h3 className="card-title mb-2">{field.title}</h3>
              <p className="card-text mb-3">{field.description}</p>
              <p className="fw-bold text-muted3">
                تعداد فناوری‌های موجود در میز:{" "}
                {field.reports_count.toLocaleString("fa-IR")}
              </p>

              {author == currentuser && (
                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                  <Button
                    className="btn-edit"
                    variant="outline-primary"
                    onClick={() => setShowEdit(true)}
                  >
                    ویرایش
                  </Button>
                  <Button
                    className="btn-delete"
                    variant="outline-danger"
                    onClick={() => setShowDelete(true)}
                  >
                    حذف
                  </Button>
                </div>
              )}

              {field.users_with_access.includes(Number(currentuser)) && (
                <Button
                  variant="success"
                  className="mt-4 w-100"
                  onClick={() => setShowForm(true)}
                >
                  ایجاد موضوع جدید
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Subject Tree Section */}
        <Col xs={12} md={8} lg={9}>
          <Card className="tree-container text-center">
            <Card.Body>
              <Container className="subject-tree-container my-4">
                {subjects.length === 0 ? (
                  <p className="text-muted">هنوز موضوعی اضافه نشده است.</p>
                ) : (
                  <SubjectTree subjects={subjects} fieldTitle={field.title} />
                )}
              </Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Reports Table */}
      <Row className="my-4">
        <Col>
          <Card>
            <Card.Body>
              <h4 className="mb-3"> لیست فناوری های میز {field.title}</h4>
              {reportsLoading ? (
                <Spinner animation="border" />
              ) : reports.length === 0 ? (
                <p className="text-muted">هنوز گزارشی ثبت نشده است.</p>
              ) : (
                <>
                  <Table striped bordered hover responsive>
  <thead>
    <tr>
      <th>عنوان</th>
      <th>خلاصه</th>
      <th>تاریخ ایجاد</th>
      <th>نویسنده</th>
    </tr>
  </thead>
  <tbody>
    {reports.map((r) => (
      <tr key={r.id}>
         <td
        style={{ cursor: "pointer", color: "inherit", textDecoration: "none" }}
        onClick={() => navigate(`/report/${r.id}`)}
        >
        {r.title}
        </td>
        <td>
  {r.summary.length > 50
    ? r.summary.substring(0, 50) + "..."
    : r.summary}
</td>
        <td>{new Date(r.created_at).toLocaleDateString("fa-IR")}</td>
        <td>{r.author.username}</td>
      </tr>
    ))}
  </tbody>
</Table>


                  {/* Pagination */}
                  {reportsTotalPages > 1 && (
                    <Pagination className="justify-content-center">
                      <Pagination.First
                        onClick={() => fetchReports(1)}
                        disabled={reportsPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => fetchReports(reportsPage - 1)}
                        disabled={reportsPage === 1}
                      />
                      {[...Array(reportsTotalPages)].map((_, idx) => (
                        <Pagination.Item
                          key={idx + 1}
                          active={idx + 1 === reportsPage}
                          onClick={() => fetchReports(idx + 1)}
                        >
                          {idx + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        onClick={() => fetchReports(reportsPage + 1)}
                        disabled={reportsPage === reportsTotalPages}
                      />
                      <Pagination.Last
                        onClick={() => fetchReports(reportsTotalPages)}
                        disabled={reportsPage === reportsTotalPages}
                      />
                    </Pagination>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      {showForm && (
        <SubjectForm
          show={showForm}
          handleClose={() => setShowForm(false)}
          fieldId={field.id}
          subjects={subjects}
        />
      )}

      <EditFieldModal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        field={field}
        token={token}
        onUpdate={() => window.location.reload()}
      />

      <DeleteConfirmModal
        show={showDelete}
        onClose={() => setShowDelete(false)}
        field={field}
      />
    </Container>
  );
};

export default FieldPage;
